import { identity } from './client';
import {
  CAPABILITY_CLASSES,
  TEMPLATE_SUBSYSTEMS,
  TEMPLATE_TERMINOLOGY_KEYS,
  type CapabilityClass,
  type HierarchyRung,
  type InstitutionTemplateDefinition,
  type TemplateCustomisationRequest,
  type TemplateSection,
  type TemplateSubsystem,
} from '@/types/institution-templates';

/**
 * The institution-templates module on campusone-identity-service — IDENTITY routes,
 * so everything goes through the identity BFF helper, not core's. Read-only plus
 * one deliberately stateless POST: /resolve/customised computes a preview and
 * persists nothing, which is why the designer can offer "experiment" at all.
 *
 * All four routes require institution_template.read; platform admins pass
 * unconditionally. A 403 carries the server's own sentence and is shown as such.
 */

const seg = encodeURIComponent;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

function normalizeRung(value: unknown, index: number): HierarchyRung | null {
  if (!isRecord(value) || typeof value.unitType !== 'string') return null;
  return {
    unitType: value.unitType,
    // The server computes depth as position + 1; trusting the array order over a
    // possibly-absent field keeps the diagram consistent with the resolver.
    depth: typeof value.depth === 'number' ? value.depth : index + 1,
    label: typeof value.label === 'string' && value.label ? value.label : value.unitType,
    required: value.required !== false,
  };
}

function normalizeSection(value: unknown): TemplateSection | null {
  if (!isRecord(value) || typeof value.code !== 'string') return null;
  return {
    code: value.code,
    label: typeof value.label === 'string' ? value.label : value.code,
    subsystem: (TEMPLATE_SUBSYSTEMS as readonly string[]).includes(value.subsystem as string)
      ? (value.subsystem as TemplateSubsystem)
      : 'NOT_APPLICABLE',
    ordinal: typeof value.ordinal === 'number' ? value.ordinal : 0,
    cycles: Array.isArray(value.cycles)
      ? value.cycles.flatMap((cycle) =>
          isRecord(cycle) && typeof cycle.code === 'string'
            ? [
                {
                  code: cycle.code,
                  label: typeof cycle.label === 'string' ? cycle.label : cycle.code,
                  levelRangeLabel:
                    typeof cycle.levelRangeLabel === 'string' ? cycle.levelRangeLabel : '',
                  ordinal: typeof cycle.ordinal === 'number' ? cycle.ordinal : 0,
                },
              ]
            : [],
        )
      : [],
  };
}

/**
 * A template is a server answer about how a kind of institution is shaped; a body
 * without a code or hierarchy is a failure, not an empty template.
 */
function normalizeTemplate(payload: unknown): InstitutionTemplateDefinition {
  if (!isRecord(payload) || typeof payload.code !== 'string' || !Array.isArray(payload.hierarchy)) {
    throw new Error('The template response was not in the expected shape.');
  }
  const academic = isRecord(payload.academicModel) ? payload.academicModel : {};
  const calendar = isRecord(academic.calendar) ? academic.calendar : {};
  const grading = isRecord(academic.grading) ? academic.grading : {};
  const progression = isRecord(academic.progression) ? academic.progression : {};

  const terminology: InstitutionTemplateDefinition['terminology'] = {};
  if (isRecord(payload.terminology)) {
    for (const key of TEMPLATE_TERMINOLOGY_KEYS) {
      const term = payload.terminology[key];
      if (typeof term === 'string' && term) terminology[key] = term;
    }
  }

  return {
    code: payload.code,
    institutionType: typeof payload.institutionType === 'string' ? payload.institutionType : '',
    educationSubsystem: (TEMPLATE_SUBSYSTEMS as readonly string[]).includes(
      payload.educationSubsystem as string,
    )
      ? (payload.educationSubsystem as TemplateSubsystem)
      : 'NOT_APPLICABLE',
    displayName: typeof payload.displayName === 'string' ? payload.displayName : payload.code,
    description: typeof payload.description === 'string' ? payload.description : '',
    capabilityClass: (CAPABILITY_CLASSES as readonly string[]).includes(
      payload.capabilityClass as string,
    )
      ? (payload.capabilityClass as CapabilityClass)
      : 'OPERATIONAL',
    hierarchy: payload.hierarchy.flatMap((rung, index) => {
      const normalized = normalizeRung(rung, index);
      return normalized ? [normalized] : [];
    }),
    auxiliaryUnitTypes: stringList(payload.auxiliaryUnitTypes),
    sections: Array.isArray(payload.sections)
      ? payload.sections.flatMap((section) => {
          const normalized = normalizeSection(section);
          return normalized ? [normalized] : [];
        })
      : [],
    levelSource: isRecord(payload.levelSource)
      ? {
          source: typeof payload.levelSource.source === 'string' ? payload.levelSource.source : '',
          institutionModel:
            typeof payload.levelSource.institutionModel === 'string'
              ? payload.levelSource.institutionModel
              : '',
          subsystems: stringList(payload.levelSource.subsystems),
        }
      : null,
    terminology,
    academicModel: {
      kind: typeof academic.kind === 'string' ? academic.kind : 'NOT_APPLICABLE',
      calendar: {
        periodType: typeof calendar.periodType === 'string' ? calendar.periodType : 'NONE',
        periodsPerYear: typeof calendar.periodsPerYear === 'number' ? calendar.periodsPerYear : 0,
        typicalStartMonth:
          typeof calendar.typicalStartMonth === 'number' ? calendar.typicalStartMonth : 0,
      },
      grading: {
        scheme: typeof grading.scheme === 'string' ? grading.scheme : 'NONE',
        passMark: typeof grading.passMark === 'number' ? grading.passMark : null,
      },
      progression: {
        mode: typeof progression.mode === 'string' ? progression.mode : 'NOT_APPLICABLE',
        repetitionAllowed: progression.repetitionAllowed === true,
      },
      certifications: stringList(academic.certifications),
    },
    defaultPositions: Array.isArray(payload.defaultPositions)
      ? payload.defaultPositions.flatMap((entry) =>
          isRecord(entry) &&
          typeof entry.positionCode === 'string' &&
          typeof entry.scopeUnitType === 'string'
            ? [
                {
                  positionCode: entry.positionCode,
                  ...(typeof entry.positionTemplateId === 'string'
                    ? { positionTemplateId: entry.positionTemplateId }
                    : {}),
                  scopeUnitType: entry.scopeUnitType,
                  scopeSelector:
                    typeof entry.scopeSelector === 'string' ? entry.scopeSelector : null,
                  capabilities: stringList(entry.capabilities),
                },
              ]
            : [],
        )
      : [],
    defaultRoleBundleCodes: stringList(payload.defaultRoleBundleCodes),
    needsReview: payload.needsReview === true,
  };
}

function normalizeTemplateList(payload: unknown): InstitutionTemplateDefinition[] {
  if (!Array.isArray(payload)) return [];
  return payload.flatMap((row) => {
    try {
      return [normalizeTemplate(row)];
    } catch {
      // One malformed row must not blank the whole catalogue page; the missing
      // template will be conspicuous by absence in a list of fifteen.
      return [];
    }
  });
}

export const institutionTemplatesApi = {
  /** All fifteen seeded templates, every institution type and subsystem. */
  list: () => identity<unknown>('/institution-templates').then(normalizeTemplateList),

  /** One template by its code — TPL_SECONDARY_SCHOOL_ANGLOPHONE and friends. */
  byCode: (code: string) =>
    identity<unknown>(`/institution-templates/${seg(code)}`).then(normalizeTemplate),

  /** The template a (type, subsystem) pair resolves to, with the NOT_APPLICABLE fallback. */
  resolve: (institutionType: string, educationSubsystem?: TemplateSubsystem) => {
    const search = new URLSearchParams({ institutionType });
    if (educationSubsystem) search.set('educationSubsystem', educationSubsystem);
    return identity<unknown>(`/institution-templates/resolve?${search}`).then(normalizeTemplate);
  },

  /**
   * The experiment endpoint. PREVIEW ONLY — the server merges the customisation
   * over the stored base, validates the result against the capability-class rules,
   * and returns the would-be template WITHOUT WRITING ANYTHING. Seeds change by
   * migration; this call cannot.
   *
   * Depth values in `hierarchy` are ignored server-side (recomputed from order),
   * but they are sent anyway so the body reads truthfully in a network tab.
   */
  previewCustomised: (body: TemplateCustomisationRequest) =>
    identity<unknown>('/institution-templates/resolve/customised', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then(normalizeTemplate),
};
